import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import BusinessDropdownIcon from '@/assets/icons/BusinessDropdownIcon';
import Search from '@/components/Common/Search';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useGetBusiness, useGetBusinesses } from 'apis/agency/hook';
import { BusinessProfileResponse } from 'apis/business/types';
import { useGetCurrentUser } from 'apis/user/hook';
import BackButtonIcon from 'assets/icons/BackButtonIcon';
import { useAgent } from 'providers/context/AgentProvider';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoChevronDownSharp } from 'react-icons/io5';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as urls from 'routes/config';
import { CacheKeys } from 'utils/cacheKeys';

export default function BusinessDropdown({ isLoading }: { isLoading?: boolean }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgencyView = location.pathname.includes('agency');
  const [page, setPage] = useState(1);
  const [businessList, setBusinessList] = useState<BusinessProfileResponse[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(null);
  const open = Boolean(anchorEl);
  const { setSelectedBusiness, setSelectedLocation } = useBusiness();
  const { setSelectedAgentId } = useAgent();
  const { data: currentUser, isLoading: loadingCurrUser } = useGetCurrentUser();
  const { data: organizationData } = useGetAgencyOrganizations();
  const [searchText, setSearchText] = useState<string | null>(null);

  const perPage = 8;
  const isFetchingRef = useRef(false);
  const prevSearchTextRef = useRef<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const isAgencyActive = organizationData?.account_status === 'active';

  const { data: businesses, isFetching } = useGetBusinesses(
    {
      page: page,
      per_page: perPage,
      include: ['business_plan', 'locations', 'business_addons'],
      filter: { name_cont: searchText, active_and_past_due: true }
    },
    {
      queryKey: ['businesses', page, searchText],
      enabled: Boolean(
        ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(currentUser?.role ?? '') && !loadingCurrUser
      )
    }
  );
  const isSearching = isFetching && page === 1;
  const storedBusinessId = JSON.parse(localStorage.getItem(CacheKeys.businessIds) || '[null]')[0];

  const { data: currentBusiness } = useGetBusiness(
    storedBusinessId,
    { include: 'business_plan,locations,telephony_providers' },
    {
      enabled: Boolean(storedBusinessId && businessList.length && !businessList.find((b) => b.id === storedBusinessId))
    }
  );

  const handleSearch = useCallback((val: string | null) => {
    if (prevSearchTextRef.current !== val) {
      prevSearchTextRef.current = val;
      setSearchText(val);
      setPage(1);
      setBusinessList([]);
    }
  }, []);

  useEffect(() => {
    if (organizationData && !isAgencyActive && !isAgencyView) {
      handleGoBackToAgencyView();
      navigate(urls.agency.base);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData, isAgencyActive, navigate]);

  useEffect(() => {
    if (businesses?.data) {
      setBusinessList((prev) => {
        const ids = new Set(prev.map((b) => b.id));
        return [...prev, ...businesses.data.filter((b) => !ids.has(b.id))];
      });
      isFetchingRef.current = false;
    }
  }, [businesses]);

  useEffect(() => {
    if (currentBusiness?.id) {
      setBusinessList((prev) => {
        if (prev.find((b) => b.id === currentBusiness.id)) return prev;
        return [currentBusiness, ...prev];
      });
    }
  }, [currentBusiness]);

  useEffect(() => {
    if (storedBusinessId) {
      setSelectedBusinessId(storedBusinessId);
    }
  }, [storedBusinessId]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    const selectedBusiness = businessList[index];
    if (!selectedBusiness) return;

    setSelectedAgentId(undefined);
    setSelectedBusiness(Number(selectedBusiness.id));
    setSelectedLocation(Number(selectedBusiness?.locations?.[0]?.id));

    localStorage.setItem(CacheKeys.businessIds, JSON.stringify([selectedBusiness.id]));
    localStorage.setItem(CacheKeys.locationIds, JSON.stringify(selectedBusiness?.locations?.map((item) => item.id)));

    setSelectedBusinessId(selectedBusiness.id); // instant UI update
    setAnchorEl(null);
    navigate('/');
  };

  const handleGoBackToAgencyView = () => {
    setSelectedBusiness(null);
    setSelectedLocation(null);

    localStorage.removeItem(CacheKeys.businessIds);
    localStorage.removeItem(CacheKeys.locationIds);

    setSelectedBusinessId(null);
    setAnchorEl(null);
  };

  const selectedBusiness = businessList.find((b) => b.id === selectedBusinessId) || currentBusiness;

  return (
    <>
      <List component='nav'>
        <ListItemButton
          disabled={!isAgencyActive}
          id='lock-button'
          aria-haspopup='listbox'
          aria-controls='lock-menu'
          aria-label='when device is locked'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickListItem}
          sx={{
            display: 'block',
            borderRadius: '8px',
            py: 0,
            px: 0,
            width: {
              xs: selectedBusiness ? 125 : 150,
              md: 240
            }
          }}
        >
          <Box display='flex' alignItems='center' gap={1} border='1px solid #ECECEC' px={1} borderRadius='8px'>
            <ListItemText>
              <Box borderRight={isLoading ? '' : '1px solid #ECECEC'}>
                <Typography fontSize={13} color='secondary'>
                  Business
                </Typography>
                {isLoading || (!isAgencyView && !selectedBusiness) ? (
                  <Skeleton variant='text' width={120} height={14} />
                ) : (
                  <Box display='flex' flexDirection='row' alignItems='center' gap={1} height={15} pb={0.5}>
                    {selectedBusiness && (
                      <Box color={theme.palette.primary.main}>
                        <BusinessDropdownIcon />
                      </Box>
                    )}
                    <Typography noWrap fontSize={14} fontWeight={{ xs: 500, md: 600 }}>
                      {!isAgencyView && selectedBusiness ? selectedBusiness.name : 'Switch to business'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </ListItemText>
            {!isLoading && <IoChevronDownSharp color='#777777' />}
          </Box>
        </ListItemButton>
      </List>
      <Menu
        id='lock-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        autoFocus={false}
        disableAutoFocusItem
        slotProps={{
          paper: {
            sx: { width: 280, maxHeight: 500 }
          },
          transition: {
            onEntered: () => {
              searchInputRef.current?.focus();
            }
          },
          list: {
            'aria-labelledby': 'lock-button',
            role: 'listbox',
            sx: {
              maxHeight: 280,
              pt: 0,
              overflowY: 'auto'
            },
            onScroll: (e: React.UIEvent<HTMLUListElement>) => {
              const target = e.currentTarget;

              const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 50;

              const totalPages = (businesses?.meta?.total_pages as number) || 1;
              const hasMore = page < totalPages;

              if (nearBottom && hasMore && !isFetchingRef.current) {
                isFetchingRef.current = true;
                setPage((prev) => prev + 1);
              }
            }
          }
        }}
      >
        <Box position='sticky' top={0} zIndex={100} bgcolor='#fff'>
          {selectedBusiness && (
            <Box
              display='flex'
              gap={1}
              alignItems='center'
              borderBottom='2px solid #F3F3F3'
              color={theme.palette.secondary.main}
              mx={1}
              py={1}
              mb={0.5}
              component={Link}
              to={urls.agency.base}
              onClick={handleGoBackToAgencyView}
            >
              <BackButtonIcon />
              <Typography fontSize={16} color='primary'>
                Switch to agency view
              </Typography>
            </Box>
          )}
          <Box
            width={{ xs: '100%', sm: 'auto' }}
            mx={1}
            pt={selectedBusiness ? 0.5 : 1.5}
            pb={1}
            minWidth={{ xs: '100%', sm: 200, md: 200 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Search
              placeholder='Search business'
              searchText={searchText}
              setSearchText={handleSearch}
              size='small'
              fullWidth
              autoFocus
              inputRef={searchInputRef}
            />
          </Box>
        </Box>

        {isSearching && (
          <Box px={1} py={1}>
            {[...Array(6)].map((_, i) => (
              <Box key={i} display='flex' alignItems='center' gap={1} py={0.8}>
                <Skeleton variant='circular' width={20} height={20} />
                <Skeleton variant='text' width='80%' height={20} />
              </Box>
            ))}
          </Box>
        )}
        {!isSearching && !businessList.length && (
          <Box py={1} textAlign='center'>
            <Typography fontSize={14} color='secondary'>
              No business found
            </Typography>
          </Box>
        )}
        {businessList.map((option, index) => (
          <MenuItem
            key={option.id}
            selected={option.id === selectedBusinessId && !isAgencyView}
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            <Stack
              color={option.id === selectedBusinessId ? '#000' : theme.palette.secondary.main}
              direction='row'
              fontSize={14}
              alignItems='center'
              gap={0.5}
              sx={{ width: '100%', overflow: 'hidden' }}
            >
              <Box
                sx={{
                  flexShrink: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BusinessDropdownIcon />
              </Box>
              <Typography noWrap>{option.name}</Typography>
            </Stack>
          </MenuItem>
        ))}
        {isFetchingRef.current && !isSearching && (
          <Typography fontSize='small' color='secondary' textAlign='center'>
            loading...
          </Typography>
        )}
      </Menu>
    </>
  );
}
